/**
 * The half of the matcher that gets better on its own.
 *
 * Everything else in this directory is a guess made in advance about how a recogniser will
 * mangle a name. This is the part that finds out. A teacher who says a name, is offered five
 * children and taps the third has just supplied a labelled training example of exactly the
 * kind the fixtures are only imitating — and until now that example was discarded the moment
 * the panel closed.
 *
 * Two things come back out of the log:
 *
 *   **Aliases.** A corrected transcript becomes a name for that child. "Zee you on" is what
 *   this recogniser produces for this child in this yard, and after one correction it is a
 *   fact rather than a phonological guess. The database trigger writes these, not this file
 *   — see the patch — because a correction that silently failed to teach would look exactly
 *   like a feature that simply never improved.
 *
 *   **Recency.** Who this teacher picked lately. Weak on purpose: enough to break a tie
 *   between two children whose names genuinely collide, never enough to put the wrong child
 *   first. A sibling permanently shadowed by whichever one was collected yesterday would be
 *   a worse failure than the one it fixes.
 *
 * Logging is deliberately fire-and-forget. A dismissal line must not stall, and must not
 * show an error, because a lookup failed to record — the teacher's actual task is finding a
 * child, and this is bookkeeping happening behind it.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import type { NameScore } from './voiceNameMatch';

export interface VoiceLookupLog {
  transcript: string;
  alternatives: string[];
  /** What was offered, best first. */
  ranked: readonly NameScore[];
  /** Enrollment the teacher actually tapped, or null when they tapped nothing. */
  chosenEnrollmentId: string | null;
  chosenStudentId: string | null;
  rosterSize: number;
}

interface LearnedNames {
  /** student_id → the transcripts teachers have corrected onto that child. */
  aliases: Map<string, string[]>;
  /** student_id → epoch ms of the last time *this* teacher picked them. */
  lastPicked: Map<string, number>;
}

const EMPTY: LearnedNames = { aliases: new Map(), lastPicked: new Map() };

export const voiceLearningKeys = {
  forStudents: (staffEmail: string, studentIds: readonly string[]) =>
    ['voice-learning', staffEmail, [...studentIds].sort().join(',')] as const,
};

/**
 * What has been learned about the children on this roster.
 *
 * Scoped to the roster in front of the teacher rather than fetched whole: the alias table is
 * small now and will not stay small, and the matcher only ever asks about children it is
 * already showing.
 */
export function useVoiceLearning(staffEmail: string | null, studentIds: readonly string[]) {
  const ids = useMemo(() => [...new Set(studentIds)].sort(), [studentIds]);

  const { data } = useQuery<LearnedNames>({
    queryKey: voiceLearningKeys.forStudents(staffEmail ?? '', ids),
    enabled: Boolean(staffEmail) && ids.length > 0,
    // The roster is stable for a session and neither table changes often. Re-fetching this
    // on every focus would be a round trip per glance at the phone.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [aliasRows, pickRows] = await Promise.all([
        supabase
          .from('student_name_aliases')
          .select('student_id, alias')
          .in('student_id', ids),
        supabase
          .from('voice_lookup_events')
          .select('chosen_student_id, created_at')
          .eq('staff_email', staffEmail as string)
          .in('chosen_student_id', ids)
          .order('created_at', { ascending: false })
          .limit(400),
      ]);

      const aliases = new Map<string, string[]>();
      for (const row of aliasRows.data ?? []) {
        const list = aliases.get(row.student_id) ?? [];
        list.push(row.alias);
        aliases.set(row.student_id, list);
      }

      const lastPicked = new Map<string, number>();
      for (const row of pickRows.data ?? []) {
        // Rows arrive newest first, so the first sighting of a child is their latest pick.
        if (!row.chosen_student_id || lastPicked.has(row.chosen_student_id)) continue;
        lastPicked.set(row.chosen_student_id, new Date(row.created_at).getTime());
      }

      return { aliases, lastPicked };
    },
  });

  return data ?? EMPTY;
}

/**
 * Record one lookup.
 *
 * `was_correction` is computed here rather than in the database because only the client
 * knows what it put on screen: a correction is "they tapped someone other than the child we
 * ranked first", and the ranking is not in the row until we write it.
 */
export function useVoiceLookupLogger(staffEmail: string | null, band: string | null) {
  // Guards against the same lookup being written twice — the panel flushes a pending lookup
  // from more than one place (a tap, a new utterance, a close) and they can race.
  const written = useRef(new Set<string>());

  return useCallback(
    (log: VoiceLookupLog) => {
      if (!staffEmail || log.transcript.trim() === '') return;

      const fingerprint = `${log.transcript}|${log.chosenEnrollmentId ?? ''}`;
      if (written.current.has(fingerprint)) return;
      written.current.add(fingerprint);
      if (written.current.size > 200) written.current.clear();

      const topKey = log.ranked[0]?.key ?? null;
      void supabase
        .from('voice_lookup_events')
        .insert({
          staff_email: staffEmail,
          raw_transcript: log.transcript,
          asr_alternatives: log.alternatives,
          band,
          roster_size: log.rosterSize,
          ranked_candidates: log.ranked.slice(0, 5).map(r => ({
            enrollment_id: r.key,
            name: r.name,
            score: Number(r.score.toFixed(4)),
          })),
          chosen_student_id: log.chosenStudentId,
          chosen_enrollment_id: log.chosenEnrollmentId,
          was_correction: log.chosenEnrollmentId !== null && log.chosenEnrollmentId !== topKey,
        })
        // Bookkeeping must never interrupt a dismissal line. If this fails the teacher's
        // task has still succeeded, and the only cost is one lost training example.
        .then(undefined, () => undefined);
    },
    [staffEmail, band],
  );
}
